import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import {
  chainDisplayLabel,
  chainLogoUrl,
  resolveChainKey,
  tokenLogoUrl,
  tokenColor,
  tokenInitials,
} from '../../utils/assetLogos';

/**
 * The merchant's payout-eligible balances, grouped by chain.
 *
 * A merchant can hold dozens of tokens across several chains, and a flat grid of
 * every one buries the balance they are looking for. So: chains first, then that
 * chain's tokens a page at a time. The chain is what a merchant thinks in, and
 * it is also what the payout form has to resolve first.
 *
 * Everything is derived from the balance response — a chain appears because the
 * merchant holds something on it, never from a hard-coded list.
 *
 * Kept deliberately dense. This sits above the payout history on a page that is
 * already long, so it is a compact picker, not a feature of its own: one row of
 * chain pills, one row of cards, and pagination folded into the header rather
 * than given its own furniture.
 *
 * `available` is already net of in-flight payouts, compliance holds and reserved
 * refunds, so what a card shows is genuinely requestable right now.
 */

// Retained as an exported constant: the list renders every asset, but callers
// and tests still reference this as the page size for any paged view of the
// same data.
export const TOKENS_PER_PAGE = 4;

const SOURCE_LABELS = {
  auto_conversion: { label: 'Auto-converted', tone: 'converted' },
  direct: { label: 'Direct', tone: 'direct' },
  mixed: { label: 'Mixed', tone: 'mixed' },
};

// The token standard is not carried in the balance data, but it is fully
// determined by the chain plus whether the asset has a contract at all.
const TOKEN_STANDARD = { bsc: 'bep20', tron: 'trc20', solana: 'spl' };

const standardFor = (row) => {
  if (!row.contractAddress) return 'native';
  return TOKEN_STANDARD[resolveChainKey(row.chain)] || 'erc20';
};

const fmtUsd = (v) =>
  `$${Number(v || 0).toLocaleString(undefined, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  })}`;

// Native assets carry 18 decimals and stablecoins 2, so a fixed precision either
// truncates a real balance or pads a tidy one with zeroes.
const fmtToken = (v) => {
  const n = Number(v || 0);
  if (!Number.isFinite(n)) return '—';
  const digits = n !== 0 && Math.abs(n) < 1 ? 6 : 2;
  return n
    .toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: digits })
    .replace(/\.0+$/, '');
};

// Asset identity is (network, contract address) — the same key the balance
// service groups on. A symbol is a label a token chooses for itself and two can
// pick the same one, including deliberately. Native assets have no contract, so
// they fall back to the symbol on their own network.
export const assetKey = (row) =>
  `${row.networkId ?? row.chain}|${row.contractAddress || `native:${row.token}`}`;

/** Balances grouped per chain, with the aggregate each chain pill needs. */
export const groupByChain = (balances) => {
  const map = new Map();

  for (const row of balances) {
    // Group on networkId, not the display label: two networks can render the
    // same name, and the payout form selects a specific network.
    const id = row.networkId ?? row.chain;
    if (!map.has(id)) {
      map.set(id, {
        id,
        chain: row.chain,
        label: chainDisplayLabel(row.chain) || row.chain,
        logo: chainLogoUrl(row.chain),
        tokens: [],
      });
    }
    map.get(id).tokens.push(row);
  }

  const groups = [...map.values()];

  for (const group of groups) {
    // Biggest balance first, so a chain's headline asset leads its page.
    group.tokens.sort((a, b) => (b.availableUsd || 0) - (a.availableUsd || 0));
    group.totalUsd = group.tokens.reduce((sum, t) => sum + (t.availableUsd || 0), 0);

    // The chain badge reflects its tokens: one source when they agree, mixed
    // otherwise. Taking the first token's source would mislabel the rest.
    const sources = new Set(group.tokens.map((t) => t.source || 'direct'));
    group.source = sources.size === 1 ? [...sources][0] : 'mixed';
  }

  // Chains carrying the most value first.
  groups.sort((a, b) => b.totalUsd - a.totalUsd);
  return groups;
};

const TokenLogo = ({ symbol }) => {
  const [failed, setFailed] = useState(false);
  const url = tokenLogoUrl(symbol);

  if (url && !failed) {
    return <img className="pa-logo" src={url} alt="" aria-hidden="true" onError={() => setFailed(true)} />;
  }
  // A long tail of tokens has no CDN logo, so initials are the normal case, not
  // an error path.
  return (
    <span className="pa-logo pa-logo-initials" style={{ background: tokenColor(symbol) }} aria-hidden="true">
      {tokenInitials(symbol)}
    </span>
  );
};

/**
 * Why an asset cannot be withdrawn right now, or null when it can.
 *
 * Shown on the card instead of letting the merchant select it, fill in an
 * amount and only then be told the request is impossible. On testnet this is
 * the difference between a usable picker and a list of dead ends: a wallet
 * collects dust, and a 0.000009 BNB balance worth nothing is not a payout.
 */
export const blockedReason = (row, minimumUsd) => {
  if (row.fullyUnpriced) return 'No USD price';
  if (minimumUsd > 0 && (row.availableUsd || 0) < minimumUsd) {
    return `Below $${minimumUsd} minimum`;
  }
  return null;
};

// CSS.escape is missing in jsdom and older Safari, and an undefined global
// thrown from inside a layout effect takes the whole page down. Network ids are
// numbers or slugs, so the fallback only has to survive, not be exhaustive.
const cssEscape = (value) =>
  typeof CSS !== 'undefined' && CSS.escape
    ? CSS.escape(String(value))
    : String(value).replace(/["\\]/g, '\\$&');

const ChainLogo = ({ src, alt }) => {
  const [failed, setFailed] = useState(false);
  if (!src || failed) return <span className="pa-chain-dot" aria-hidden="true" />;
  return <img className="pa-chain-ico" src={src} alt={alt || ''} onError={() => setFailed(true)} />;
};

/**
 * One chain's rows, as a <tbody> inside the shared table.
 *
 * Each chain used to render its own complete <table>. With auto layout every
 * one of them sized its columns independently, so "Balance" sat at a different
 * x-position in each chain and the headers repeated down the page. A single
 * table with one header row and a tbody per chain keeps every column on the
 * same grid and states the column names once.
 */
const ChainGroup = ({ group, selectedKey, onSelect, minimumUsd, defaultOpen = true }) => {
  const [open, setOpen] = useState(defaultOpen);
  const bodyId = `pa-group-${group.id}`;

  return (
    <tbody className="pa-group" id={bodyId}>
      <tr className="pa-grouprow">
        <td colSpan={5}>
          <button
            type="button"
            className="pa-grouphead"
            onClick={() => setOpen((v) => !v)}
            aria-expanded={open}
            aria-controls={bodyId}
          >
            <ChainLogo src={group.logo} alt="" />
            <span className="pa-group-name">{group.label}</span>
            <span className="pa-group-count">
              {group.tokens.length} {group.tokens.length === 1 ? 'asset' : 'assets'}
            </span>
            {/* The chain's own total, so a collapsed group still answers "how
                much is on this network?" without being expanded. */}
            <span className="pa-group-total">{fmtUsd(group.totalUsd)}</span>
            <span className={`pa-chev${open ? ' pa-chev-up' : ''}`} aria-hidden="true">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2"
                   strokeLinecap="round" strokeLinejoin="round">
                <path d="M6 9l6 6 6-6" />
              </svg>
            </span>
          </button>
        </td>
      </tr>

      {open &&
        group.tokens.map((row) => {
          const source = SOURCE_LABELS[row.source] || SOURCE_LABELS.direct;
          const key = assetKey(row);
          const isSelected = selectedKey === key;
          const blocked = blockedReason(row, minimumUsd);

          return (
            <tr key={key} className={isSelected ? 'pa-tr-on' : undefined}>
              <td data-label="Asset">
                <span className="pa-asset">
                  <span className="pa-logos">
                    <TokenLogo symbol={row.token} />
                    {group.logo && (
                      <img className="pa-chain-badge" src={group.logo} alt="" aria-hidden="true" />
                    )}
                  </span>
                  <span className="pa-id">
                    <span className="pa-id-top">
                      <span className="pa-symbol">{row.token}</span>
                      <span className={`pa-tag pa-tag-${source.tone}`}>{source.label}</span>
                    </span>
                    <span className="pa-standard">{standardFor(row)}</span>
                  </span>
                </span>
              </td>

              <td className="pa-num" data-label="Balance">
                <span className="pa-bal">{fmtToken(row.available)}</span>
                <span className="pa-bal-sym">{row.token}</span>
              </td>

              <td className="pa-num" data-label="Value (USD)">
                {/* An asset with no USD price must not render as "$0" — that
                    reads as worthless when the truth is only that we could not
                    price it. */}
                {row.fullyUnpriced ? (
                  <span className="pa-usd-na">No price</span>
                ) : (
                  <span className="pa-usd">
                    &asymp; {fmtUsd(row.availableUsd)}
                    {row.unpricedToken > 0 && (
                      <em
                        className="pa-partial"
                        title={`${fmtToken(row.unpricedToken)} ${row.token} of this balance has no USD price`}
                      >
                        {fmtToken(row.unpricedToken)} unpriced
                      </em>
                    )}
                  </span>
                )}
              </td>

              <td data-label="Status">
                {/* The unavailable state names its own cause. A bare "Not
                    available" leaves the merchant to guess whether the problem
                    is theirs to fix. */}
                <span className={`pa-status${blocked ? ' pa-status-off' : ''}`}>
                  <span className="pa-status-dot" aria-hidden="true" />
                  {blocked || 'Available'}
                </span>
              </td>

              <td className="pa-act" data-label="Action">
                <button
                  type="button"
                  className={`pa-withdraw${isSelected ? ' pa-withdraw-on' : ''}`}
                  onClick={() => onSelect?.(row)}
                  disabled={Boolean(blocked)}
                  aria-pressed={isSelected}
                  title={blocked ? `${row.token}: ${blocked}` : undefined}
                >
                  {isSelected ? 'Selected' : 'Withdraw'}
                </button>
              </td>
            </tr>
          );
        })}
    </tbody>
  );
};

const AvailableAssets = ({
  balances = [],
  loading = false,
  selectedKey = null,
  onSelect,
  minimumUsd = 0,
}) => {
  const groups = useMemo(() => groupByChain(balances), [balances]);
  const [activeChain, setActiveChain] = useState('all');
  const tabsRef = useRef(null);
  // The underline is one element that slides, not a border that switches on and
  // off per tab: a border can only appear and disappear, and the movement
  // between two tabs is the thing that makes the change feel connected.
  const [ink, setInk] = useState({ left: 0, width: 0, ready: false });

  const assetCount = groups.reduce((sum, g) => sum + g.tokens.length, 0);

  // A chain the merchant no longer holds anything on must not stay selected, or
  // the panel area goes blank with no way back except clicking "All".
  const active = activeChain !== 'all' && groups.some((g) => String(g.id) === activeChain)
    ? activeChain
    : 'all';
  const shown = active === 'all' ? groups : groups.filter((g) => String(g.id) === active);

  const measureInk = useCallback(() => {
    const host = tabsRef.current;
    if (!host) return;
    const el = host.querySelector(`[data-tabkey="${cssEscape(active)}"]`);
    if (!el) return;
    // offsetLeft is relative to the scrolling container, so the underline stays
    // pinned to its tab when the row is scrolled sideways.
    setInk({ left: el.offsetLeft, width: el.offsetWidth, ready: true });
  }, [active]);

  useLayoutEffect(() => {
    measureInk();
  }, [measureInk, groups.length, loading]);

  useEffect(() => {
    // Tab widths depend on the label, which reflows with the viewport, and web
    // fonts land after first paint and change them again.
    window.addEventListener('resize', measureInk);
    const fonts = typeof document !== 'undefined' && document.fonts;
    if (fonts?.ready) fonts.ready.then(measureInk).catch(() => {});
    return () => window.removeEventListener('resize', measureInk);
  }, [measureInk]);

  // Keep the chosen tab visible when the row is too narrow to show them all.
  useEffect(() => {
    const host = tabsRef.current;
    const el = host?.querySelector(`[data-tabkey="${cssEscape(active)}"]`);
    el?.scrollIntoView({ inline: 'nearest', block: 'nearest', behavior: 'smooth' });
  }, [active]);

  if (loading) {
    return (
      <section className="pa">
        <div className="pa-head">
          <h2>Available assets</h2>
        </div>
        <div className="pa-list">
          {[0, 1, 2].map((i) => (
            <div key={i} className="pa-skeleton-row" />
          ))}
        </div>
        <style jsx>{styles}</style>
      </section>
    );
  }

  return (
    <section className="pa">
      <div className="pa-head">
        <h2>Available assets</h2>
        <span className="pa-sub">Choose what to withdraw</span>
        <span className="pa-count">
          {assetCount} {assetCount === 1 ? 'asset' : 'assets'}
        </span>
      </div>

      {groups.length === 0 ? (
        <p className="pa-empty">
          No payout-eligible balance right now. Amounts committed to a pending payout, a hold or a
          refund are not shown here.
        </p>
      ) : (
        <>
          {/* Network tabs. Only chains the merchant actually holds something on
              appear — the list is derived from the balance response, never from
              a fixed roster of supported networks. The row scrolls rather than
              wrapping, so adding a chain never changes the block's height. */}
          <div className="pa-tabs" role="tablist" aria-label="Filter assets by network" ref={tabsRef}>
            <button
              type="button"
              role="tab"
              data-tabkey="all"
              aria-selected={active === 'all'}
              className={`pa-tab${active === 'all' ? ' pa-tab-on' : ''}`}
              onClick={() => setActiveChain('all')}
            >
              <span className="pa-tab-all" aria-hidden="true" />
              All
              <span className="pa-tab-count">{assetCount}</span>
            </button>
            {groups.map((g) => (
              <button
                key={g.id}
                type="button"
                role="tab"
                data-tabkey={String(g.id)}
                aria-selected={active === String(g.id)}
                className={`pa-tab${active === String(g.id) ? ' pa-tab-on' : ''}`}
                onClick={() => setActiveChain(String(g.id))}
              >
                <ChainLogo src={g.logo} alt="" />
                {g.label}
                <span className="pa-tab-count">{g.tokens.length}</span>
              </button>
            ))}
            {/* Hidden until measured, so it cannot animate in from x=0 on the
                first paint and slide across the row on arrival. */}
            <span
              className={`pa-tab-ink${ink.ready ? ' pa-tab-ink-ready' : ''}`}
              aria-hidden="true"
              style={{
                transform: `translateX(${ink.left}px)`,
                width: `${ink.width}px`,
              }}
            />
          </div>

          {/* Keyed by the active tab so React remounts the list on a switch and
              the CSS enter animation replays; without the key the rows would
              swap in place with no transition at all. */}
          <div className="pa-tablewrap" key={active}>
            <table className="pa-table">
              <colgroup>
                {/* Fixed proportions: with auto layout the asset column collapsed
                    to its content and the two money columns bunched together in
                    the middle of the row. */}
                <col style={{ width: '32%' }} />
                <col style={{ width: '17%' }} />
                <col style={{ width: '17%' }} />
                <col style={{ width: '17%' }} />
                <col style={{ width: '17%' }} />
              </colgroup>
              <thead>
                <tr>
                  <th>Asset</th>
                  <th className="pa-num">Balance</th>
                  <th className="pa-num">Value (USD)</th>
                  <th>Status</th>
                  <th className="pa-act">Action</th>
                </tr>
              </thead>
              {shown.map((g, i) => (
              <ChainGroup
                // Keyed by the active tab as well as the chain, so switching
                // tabs remounts the panel and its open state is taken from
                // defaultOpen again. Keyed on the chain alone, a panel the
                // merchant had collapsed under "All" would stay collapsed after
                // they clicked straight to that chain — a tab that appears to
                // show nothing.
                key={`${active}-${g.id}`}
                group={g}
                selectedKey={selectedKey}
                onSelect={onSelect}
                minimumUsd={minimumUsd}
                // A single chain is always expanded. Under "All" the first is
                // expanded and the rest collapse to their header, which still
                // carries the chain's name, asset count and total — a summary
                // rather than a wall of rows above the payout history.
                defaultOpen={active !== 'all' || groups.length <= 3 || i === 0}
              />
              ))}
            </table>
          </div>
        </>
      )}

      <style jsx>{styles}</style>
    </section>
  );
};

const styles = `
  /* NOTE: this project has no styled-jsx transform, so <style jsx> emits a
     plain global <style> tag. Every selector here is therefore GLOBAL, which is
     why all of them are prefixed .pa- and none of them style a bare element. */

  /* Matches the page's other sections (.pro-table-wrap): same surface, border
     and radius tokens, so this does not read as a different design. */
  .pa {
    background: var(--ent-card, var(--surface));
    border: 1px solid var(--ent-border, var(--line));
    border-radius: var(--ent-radius, var(--r-md));
    margin-bottom: 18px;
    overflow: hidden;
  }

  .pa-head {
    display: flex;
    align-items: baseline;
    gap: 10px;
    flex-wrap: wrap;
    padding: 16px 18px 14px;
  }
  .pa-head h2 {
    margin: 0;
    font-size: 16px;
    font-weight: 700;
    letter-spacing: -0.01em;
    color: var(--ent-text, var(--t-900));
  }
  .pa-sub { font-size: 13px; color: var(--ent-muted, var(--t-500)); }
  .pa-count {
    margin-left: auto;
    font-size: 12px;
    font-weight: 600;
    color: var(--ent-text-2, var(--t-700));
    background: var(--ent-card-alt, var(--surface-2));
    border: 1px solid var(--ent-border, var(--line));
    border-radius: 999px;
    padding: 3px 10px;
  }
  .pa-empty {
    margin: 0;
    padding: 0 18px 18px;
    font-size: 13px;
    line-height: 1.6;
    color: var(--ent-muted, var(--t-500));
  }

  /* ---- network tabs ---- */
  .pa-tabs {
    position: relative;
    display: flex;
    align-items: stretch;
    gap: 2px;
    padding: 0 12px;
    border-bottom: 1px solid var(--ent-border, var(--line));
    overflow-x: auto;
    scrollbar-width: none;
  }
  /* The moving underline. Absolutely placed inside the scrolling row, so it
     travels with the tabs when the row is scrolled rather than floating over
     them. Width is animated as well as position: the tabs are different widths
     and a fixed-width marker would detach from its label mid-slide. */
  .pa-tab-ink {
    position: absolute;
    left: 0;
    bottom: 0;
    height: 2px;
    border-radius: 2px 2px 0 0;
    background: var(--brand-600, #6d5df6);
    pointer-events: none;
    opacity: 0;
  }
  /* Transitions are switched on only once the first measurement has landed.
     Before that the marker is still at x=0, and a transition would have it
     slide in across the whole row on arrival. */
  .pa-tab-ink-ready {
    opacity: 1;
    transition: transform 0.28s cubic-bezier(0.22, 1, 0.36, 1),
                width 0.28s cubic-bezier(0.22, 1, 0.36, 1);
  }
  .pa-tabs::-webkit-scrollbar { display: none; }
  .pa-tab {
    display: inline-flex;
    align-items: center;
    gap: 7px;
    flex: 0 0 auto;
    height: 44px;
    padding: 0 12px;
    border: none;
    background: none;
    font: inherit;
    font-size: 13px;
    font-weight: 500;
    color: var(--ent-text-2, var(--t-700));
    cursor: pointer;
    white-space: nowrap;
    transition: color 0.2s ease;
  }
  .pa-tab:hover { color: var(--ent-text, var(--t-900)); }
  .pa-tab-on {
    color: var(--brand-600, #6d5df6);
    font-weight: 600;
  }
  .pa-tab-count {
    transition: background 0.2s ease, color 0.2s ease;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 18px;
    height: 18px;
    padding: 0 5px;
    border-radius: 999px;
    background: var(--ent-card-alt, var(--surface-2));
    color: var(--ent-muted, var(--t-500));
    font-size: 11px;
    font-weight: 600;
    font-variant-numeric: tabular-nums;
  }
  .pa-tab-on .pa-tab-count {
    background: var(--brand-100, #ece9ff);
    color: var(--brand-700, #5847d6);
  }
  /* Stand-in mark for the "All" tab, which has no chain of its own. */
  .pa-tab-all {
    width: 16px; height: 16px; border-radius: 5px; flex: none;
    background: linear-gradient(135deg, var(--brand-500, #8b7bff), var(--brand-700, #5847d6));
    opacity: 0.85;
  }
  .pa-chain-ico { width: 16px; height: 16px; border-radius: 50%; flex: none; object-fit: contain; }
  .pa-chain-dot {
    width: 16px; height: 16px; border-radius: 50%; flex: none;
    background: var(--ent-border-strong, var(--line-strong));
  }

  /* ---- one table, a tbody per chain ---- */
  .pa-tablewrap { overflow-x: auto; }
  .pa-table {
    width: 100%;
    min-width: 720px;
    border-collapse: collapse;
    table-layout: fixed;
  }
  .pa-table thead th {
    text-align: left;
    padding: 11px 18px;
    font-size: 11.5px;
    font-weight: 600;
    letter-spacing: 0.02em;
    color: var(--ent-muted, var(--t-500));
    background: var(--ent-card-alt, var(--surface-2));
    border-bottom: 1px solid var(--ent-border, var(--line));
    white-space: nowrap;
  }
  .pa-table tbody td {
    padding: 14px 18px;
    font-size: 13.5px;
    color: var(--ent-text, var(--t-900));
    vertical-align: middle;
  }
  .pa-table .pa-num { text-align: left; font-variant-numeric: tabular-nums; white-space: nowrap; }
  .pa-table .pa-act { text-align: right; }

  /* ---- chain band ---- */
  /* A full-width row rather than a separate card: it keeps every chain's
     columns on the one grid the table already defines, which separate tables
     could not do — each sized its own columns and none of them lined up. */
  .pa-grouprow > td { padding: 0; }
  .pa-grouphead {
    display: flex;
    align-items: center;
    gap: 10px;
    width: 100%;
    padding: 10px 18px;
    border: none;
    border-top: 1px solid var(--ent-border, var(--line));
    background: var(--bg-inset, #eff0f8);
    font: inherit;
    text-align: left;
    cursor: pointer;
    transition: background 0.15s ease;
  }
  .pa-group:first-of-type .pa-grouphead { border-top: none; }
  .pa-grouphead:hover { background: var(--brand-100, #ece9ff); }
  .pa-group-name {
    font-size: 13.5px;
    font-weight: 700;
    color: var(--ent-text, var(--t-900));
    letter-spacing: -0.01em;
  }
  .pa-group-count {
    font-size: 11px;
    font-weight: 600;
    color: var(--ent-muted, var(--t-500));
    background: var(--ent-card, var(--surface));
    border: 1px solid var(--ent-border, var(--line));
    border-radius: 999px;
    padding: 2px 9px;
  }
  .pa-group-total {
    margin-left: auto;
    font-size: 13px;
    font-weight: 700;
    color: var(--ent-text, var(--t-900));
    font-variant-numeric: tabular-nums;
  }
  .pa-chev { display: inline-flex; width: 16px; height: 16px; color: var(--ent-muted, var(--t-500)); transition: transform 0.18s ease; }
  .pa-chev svg { width: 16px; height: 16px; }
  .pa-chev-up { transform: rotate(180deg); }

  /* Asset rows: hairline between them, none on the last before the next band. */
  .pa-group tr:not(.pa-grouprow) td { border-bottom: 1px solid var(--ent-border, var(--line)); }
  .pa-group tr:not(.pa-grouprow):last-child td { border-bottom: none; }
  .pa-group tr:not(.pa-grouprow):hover td { background: var(--ent-card-alt, var(--surface-2)); }
  .pa-tr-on td { background: var(--brand-100, #ece9ff) !important; }

  /* Groups stagger in on a tab switch. Capped: past the sixth the delay would
     outlast the transition and the last rows would visibly lag. */
  .pa-group { animation: pa-swap 0.34s cubic-bezier(0.22, 1, 0.36, 1) both; }
  .pa-group:nth-of-type(2) { animation-delay: 0.04s; }
  .pa-group:nth-of-type(3) { animation-delay: 0.08s; }
  .pa-group:nth-of-type(4) { animation-delay: 0.12s; }
  .pa-group:nth-of-type(5) { animation-delay: 0.16s; }
  .pa-group:nth-of-type(n + 6) { animation-delay: 0.2s; }
  @keyframes pa-swap {
    from { opacity: 0; transform: translateY(6px); }
    to   { opacity: 1; transform: translateY(0); }
  }

  /* asset cell */
  .pa-asset { display: flex; align-items: center; gap: 11px; min-width: 0; }
  .pa-logos { position: relative; flex: none; display: inline-flex; }
  .pa-logo {
    width: 32px; height: 32px; border-radius: 50%; object-fit: contain;
    background: var(--ent-card, var(--surface));
    display: inline-flex; align-items: center; justify-content: center;
  }
  /* A long tail of tokens has no CDN logo, so initials are a normal state and
     must look deliberate rather than broken. */
  .pa-logo-initials {
    color: #fff; font-size: 11px; font-weight: 700; letter-spacing: 0.02em;
  }
  .pa-chain-badge {
    position: absolute; right: -2px; bottom: -2px;
    width: 15px; height: 15px; border-radius: 50%;
    border: 2px solid var(--ent-card, var(--surface));
    background: var(--ent-card, var(--surface));
    object-fit: contain;
  }
  .pa-id { display: flex; flex-direction: column; gap: 1px; min-width: 0; }
  .pa-id-top { display: inline-flex; align-items: center; gap: 7px; }
  .pa-symbol { font-size: 13.5px; font-weight: 700; color: var(--ent-text, var(--t-900)); }
  .pa-standard {
    font-size: 11.5px;
    color: var(--ent-muted, var(--t-500));
    text-transform: lowercase;
  }
  .pa-tag {
    font-size: 9.5px; font-weight: 700; letter-spacing: 0.05em; text-transform: uppercase;
    padding: 2px 6px; border-radius: 999px; white-space: nowrap;
  }
  .pa-tag-direct { background: var(--ent-card-alt, var(--surface-2)); color: var(--ent-muted, var(--t-500)); }
  .pa-tag-converted { background: var(--brand-100, #ece9ff); color: var(--brand-700, #5847d6); }
  .pa-tag-mixed { background: var(--warn-soft, #fef3e0); color: var(--warn-ink, #b9760a); }

  /* balance + value */
  .pa-bal { font-size: 14px; font-weight: 700; }
  .pa-bal-sym { font-size: 11.5px; color: var(--ent-muted, var(--t-500)); margin-left: 5px; }
  .pa-usd { color: var(--ent-text-2, var(--t-700)); }
  .pa-usd-na { color: var(--ent-muted, var(--t-500)); font-style: italic; }
  .pa-partial {
    display: block; font-style: normal; font-size: 11px;
    color: var(--warn-ink, #b9760a);
  }

  /* status */
  .pa-status {
    display: inline-flex; align-items: center; gap: 7px;
    font-size: 12.5px; font-weight: 500;
    color: var(--ok-ink, #0a8f64);
    white-space: nowrap;
  }
  .pa-status-dot {
    width: 7px; height: 7px; border-radius: 50%; flex: none;
    background: currentColor;
  }
  .pa-status-off { color: var(--ent-muted, var(--t-500)); }

  /* action */
  .pa-withdraw {
    display: inline-flex; align-items: center; justify-content: center;
    height: 32px; padding: 0 16px;
    border: none; border-radius: 8px;
    background: var(--brand-600, #6d5df6);
    color: #fff;
    font: inherit; font-size: 12.5px; font-weight: 600;
    cursor: pointer;
    transition: background 0.15s, opacity 0.15s;
  }
  .pa-withdraw:hover:not(:disabled) { background: var(--brand-700, #5847d6); }
  .pa-withdraw:disabled { opacity: 0.4; cursor: not-allowed; }
  .pa-withdraw-on { background: var(--brand-700, #5847d6); }

  .pa-skeleton-row {
    height: 56px; border-radius: 10px; margin: 0 18px 10px;
    background: linear-gradient(90deg, var(--surface-2, #f3f3f1) 25%, var(--bg-inset, #eff0f8) 37%, var(--surface-2, #f3f3f1) 63%);
    background-size: 400% 100%;
    animation: pa-shimmer 1.4s ease-in-out infinite;
  }
  @keyframes pa-shimmer { 0% { background-position: 100% 0; } 100% { background-position: 0 0; } }

  @media (prefers-reduced-motion: reduce) {
    .pa-skeleton-row { animation: none; }
    .pa-group { animation: none; }
    .pa-tab-ink-ready { transition: none; }
    .pa-chev { transition: none; }
  }

  /* ---- narrow: the table scrolls rather than restacking ----
     The stacked-card version turned every asset into a four-line block, and a
     merchant comparing two balances then had to scroll between them. A table
     this narrow (five short columns) is more usable scrolled sideways, with the
     asset column pinned so the row never loses its label. */
  @media (max-width: 760px) {
    .pa-table { min-width: 660px; }
    .pa-table thead th, .pa-table tbody td { padding: 12px 14px; }
    .pa-grouphead { padding: 10px 14px; }
    .pa-group-total { font-size: 12px; }
    span.pa-status.pa-status-off{white-space:unset;}
  }
`;

export default AvailableAssets;
